import { useCallback, useContext, useState } from 'react'
import api from '../../api'
import { ProjectContext } from '../../contexts'
import { ProjectAdmin, projectRoles } from '../../types'
import Button from '../../ui/Button'
import { ArchiveIcon, EditIcon, PlusIcon } from '../../ui/icons'
import Menu, { MenuItem } from '../../ui/Menu'
import { Table, useSearchTableQueryState } from '../../components'
import { snakeToTitle } from '../../utils'
import TeamInvite from './TeamInvite'
import { useTranslation } from 'react-i18next'
import { MultiSelect } from '../../ui/form/MultiSelect'

type EditFormData = Pick<ProjectAdmin, 'admin_id' | 'role'> & { id?: number }

export default function Teams() {
    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const state = useSearchTableQueryState(useCallback(async params => await api.projectAdmins.search(project.id, params), [project]))
    const [editing, setEditing] = useState<Partial<EditFormData>>()

    return (
        <>
            <Table
                {...state}
                title={t('team')}
                actions={
                    <Button
                        icon={<PlusIcon />}
                        size="small"
                        onClick={() => setEditing({
                            admin_id: undefined,
                            role: 'support',
                        })}
                    >{t('add_team_member')}</Button>
                }
                columns={[
                    { key: 'first_name', title: t('first_name') },
                    { key: 'last_name', title: t('last_name') },
                    { key: 'email', title: t('email') },
                    {
                        key: 'role',
                        title: t('role'),
                        cell: ({ item }) => snakeToTitle(item.role),
                    },
                    {
                        key: 'options',
                        title: t('options'),
                        align: 'end',
                        cell: ({ item }) => (
                            <Menu size="small">
                                <MenuItem
                                    icon={<ArchiveIcon />}
                                    onClick={async () => {
                                        await api.projectAdmins.remove(item.project_id, item.admin_id)
                                        await state.reload()
                                    }}
                                >
                                    {t('remove')}
                                </MenuItem>
                                <MenuItem icon={<EditIcon />} onClick={() => setEditing(item)}>
                                    {t('edit')}
                                </MenuItem>
                            </Menu>
                        ),
                    },
                ]}
                itemKey={({ item }) => item.id}
                onSelectRow={setEditing}
                enableSearch
                filters={[
                    <MultiSelect
                        key="role"
                        size="regular"
                        options={projectRoles.map(role => ({ key: role, label: snakeToTitle(role) }))}
                        value={state.params.filter?.role}
                        onChange={value => state.setParams({
                            ...state.params,
                            filter: {
                                ...state.params.filter,
                                role: value.length ? value : undefined,
                            },
                        })}
                        placeholder={t('role')}
                        toValue={v => v.key}
                    />,
                ]}
            />

            <TeamInvite
                member={editing}
                onMember={async () => {
                    await state.reload()
                    setEditing(undefined)
                }}
                open={Boolean(editing)}
                onClose={() => setEditing(undefined)}
            />
        </>
    )
}
